import api from 'config/api.config';
import { AxiosResponse } from 'axios';
import { logger } from 'utils/logger';
import { getErrorMessage } from 'utils/errors';

// Define interface for API response data (generic, adjust based on actual response)
interface InstagramApiResponse<T = unknown> {
  data: T;
  message?: string;
}

// Function to add an Instagram account with authentication
export const addInstagramAccount = async (
  username: string,
  password: string,
  country: string,
  countryCode: string,
  locale: string,
  timezoneOffset: number
): Promise<InstagramApiResponse> => {
  try {
    const response: AxiosResponse<InstagramApiResponse> = await api.post(
      '/api/instagram/auth-instagram',
      { username, password, country, countryCode, locale, timezoneOffset }
    );

    return response.data;
  } catch (error: unknown) {
    logger.error('Error adding Instagram account:', (error as { message?: string })?.message ?? 'Unknown error');
    throw new Error(getErrorMessage(error, 'Failed to add Instagram account'));
  }
};

// Function to send Instagram account code
export const sendInstagramAccountCode = async (code: string): Promise<InstagramApiResponse> => {
  try {
    const response: AxiosResponse<InstagramApiResponse> = await api.post(
      '/api/instagram/auth-instagram-code',
      { code }
    );

    return response.data;
  } catch (error: unknown) {
    logger.error('Error sending Instagram account code:', (error as { message?: string })?.message ?? 'Unknown error');
    throw new Error(getErrorMessage(error, 'Failed to send Instagram account code'));
  }
};


// Function to update Instagram account connection status.
// Optional errorMessage is stored as connection_error_reason on the account
// (only honored by the API when connection_status === 'connection_issue').
export const updateConnectionStatus = async (
  username: string,
  connectionStatus: string,
  errorMessage?: string
): Promise<InstagramApiResponse> => {
  try {
    const payload: Record<string, string> = { username, connection_status: connectionStatus };
    if (errorMessage && errorMessage.trim()) payload.error_message = errorMessage.trim();
    const response: AxiosResponse<InstagramApiResponse> = await api.put(
      '/api/instagram/update-connection-status',
      payload
    );

    return response.data;
  } catch (error: unknown) {
    logger.error('Error updating connection status:', (error as { message?: string })?.message ?? 'Unknown error');
    throw new Error(getErrorMessage(error, 'Failed to update connection status'));
  }
};

export const sendInstagramAuthMethod = async (type: string): Promise<InstagramApiResponse> => {
  try {
    const response: AxiosResponse<InstagramApiResponse> = await api.post(
      '/api/instagram/2fa-method',
      { method:type }
    );

    return response.data;
  } catch (error: unknown) {
    logger.error('Error sending Instagram account code:', (error as { message?: string })?.message ?? 'Unknown error');
    throw new Error(getErrorMessage(error, 'Failed to send Instagram account code'));
  }
};
